import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { X, UserPlus, Mail, ChevronDown, Users, Settings } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DistributionRecipient {
  type: 'member' | 'external';
  id?: string;
  name: string;
  email: string;
  role?: string;
}

interface DistributionListSelectorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  unaId?: string;
  required?: boolean;
  helpText?: string;
  allowExternal?: boolean;
}

export function DistributionListSelector({
  label,
  value,
  onChange,
  unaId,
  required = false,
  helpText,
  allowExternal = true,
}: DistributionListSelectorProps) {
  const [members, setMembers] = useState<any[]>([]);
  const [recipients, setRecipients] = useState<DistributionRecipient[]>([]);
  const [open, setOpen] = useState(false);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [externalName, setExternalName] = useState("");
  const [externalEmail, setExternalEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Load UNA members
  useEffect(() => {
    if (unaId) {
      loadMembers();
    }
  }, [unaId]);

  // Parse initial value
  useEffect(() => {
    if (value) {
      parseDistributionList(value);
    }
  }, [value]);

  const loadMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('una_members')
        .select('id, full_name, email, member_role')
        .eq('una_id', unaId)
        .eq('status', 'active')
        .order('full_name');

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load members');
    }
  };

  const parseDistributionList = (distributionString: string) => {
    if (!distributionString) {
      setRecipients([]);
      return;
    }

    // Parse both old format (string) and new format
    const lines = distributionString.split('\n').filter(line => line.trim());
    const parsed: DistributionRecipient[] = [];

    lines.forEach(line => {
      // Format: "Name <email@example.com>" or just "email@example.com"
      const match = line.match(/^(.+?)\s*<(.+?)>$/) || line.match(/^(\S+@\S+)$/);
      
      if (match) {
        const name = match[2] ? match[1].trim() : match[1].split('@')[0];
        const email = match[2] || match[1];
        
        // Check if this is a member
        const member = members.find(m => m.email === email);
        
        parsed.push({
          type: member ? 'member' : 'external',
          id: member?.id,
          name: member?.full_name || name,
          email: email,
          role: member?.member_role,
        });
      }
    });

    setRecipients(parsed);
  };

  const formatDistributionList = (recipientList: DistributionRecipient[]): string => {
    return recipientList
      .map(r => `${r.name} <${r.email}>`)
      .join('\n');
  };

  const addMember = (member: any) => {
    const exists = recipients.some(r => r.email === member.email);
    if (exists) {
      toast.error('This member is already in the distribution list');
      return;
    }

    const newRecipient: DistributionRecipient = {
      type: 'member',
      id: member.id,
      name: member.full_name,
      email: member.email,
      role: member.member_role,
    };

    const updatedRecipients = [...recipients, newRecipient];
    setRecipients(updatedRecipients);
    onChange(formatDistributionList(updatedRecipients));
    setOpen(false);
  };

  const addExternalContact = () => {
    if (!externalName.trim() || !externalEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(externalEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const exists = recipients.some(r => r.email === externalEmail);
    if (exists) {
      toast.error('This email is already in the distribution list');
      return;
    }

    const newRecipient: DistributionRecipient = {
      type: 'external',
      name: externalName.trim(),
      email: externalEmail.trim(),
    };

    const updatedRecipients = [...recipients, newRecipient];
    setRecipients(updatedRecipients);
    onChange(formatDistributionList(updatedRecipients));
    
    // Reset form
    setExternalName("");
    setExternalEmail("");
    setExternalDialogOpen(false);
    toast.success('External contact added');
  };

  const removeRecipient = (email: string) => {
    const updatedRecipients = recipients.filter(r => r.email !== email);
    setRecipients(updatedRecipients);
    onChange(formatDistributionList(updatedRecipients));
  };

  const getRoleBadgeColor = (role: string) => {
    const roleColors: { [key: string]: string } = {
      'treasury_secretary': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      'managing_member': 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      'board_member_active': 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      'operations_manager': 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    };
    return roleColors[role] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatRoleLabel = (role: string) => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="distribution-list">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      
      {helpText && (
        <p className="text-sm text-muted-foreground">{helpText}</p>
      )}

      {/* Selected Recipients */}
      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-muted/30">
          {recipients.map((recipient, index) => (
            <Badge
              key={`${recipient.email}-${index}`}
              variant="secondary"
              className={cn(
                "pl-3 pr-1 py-1.5 text-sm gap-2",
                recipient.type === 'external' && "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
              )}
            >
              <span className="flex items-center gap-1.5">
                {recipient.type === 'member' ? (
                  <Users className="h-3 w-3" />
                ) : (
                  <Mail className="h-3 w-3" />
                )}
                <span className="font-medium">{recipient.name}</span>
                {recipient.role && (
                  <span className="text-xs opacity-70">
                    ({formatRoleLabel(recipient.role)})
                  </span>
                )}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-background/80"
                onClick={() => removeRecipient(recipient.email)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add Recipients Buttons */}
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between"
              disabled={!unaId}
            >
              <span className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Add Member or Officer
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command>
              <CommandInput placeholder="Search members..." />
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {members.map((member) => {
                  const isSelected = recipients.some(r => r.email === member.email);
                  return (
                    <CommandItem
                      key={member.id}
                      onSelect={() => addMember(member)}
                      disabled={isSelected}
                      className={cn(isSelected && "opacity-50")}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col">
                          <span className="font-medium">{member.full_name}</span>
                          <span className="text-xs text-muted-foreground">{member.email}</span>
                        </div>
                        {member.member_role && (
                          <Badge variant="secondary" className={cn("text-xs", getRoleBadgeColor(member.member_role))}>
                            {formatRoleLabel(member.member_role)}
                          </Badge>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>

        {allowExternal && (
          <Dialog open={externalDialogOpen} onOpenChange={setExternalDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline">
                <UserPlus className="h-4 w-4 mr-2" />
                Add External
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add External Contact</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="external-name">Name</Label>
                  <Input
                    id="external-name"
                    placeholder="John Doe"
                    value={externalName}
                    onChange={(e) => setExternalName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="external-email">Email Address</Label>
                  <Input
                    id="external-email"
                    type="email"
                    placeholder="john@example.com"
                    value={externalEmail}
                    onChange={(e) => setExternalEmail(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExternalDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={addExternalContact}>
                  Add Contact
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {recipients.length === 0 && (
        <p className="text-sm text-muted-foreground italic">
          No recipients selected. Documents will not be automatically distributed.
        </p>
      )}
    </div>
  );
}
